<template>
  <div v-if="!showDetails" class="cashbox-list">
    <searcher @compose-filters="loadFilteredWithdrawals"
              @clear-view="clearView"
              :filters="restoreFilters ? restoreFilters : null"/>

    <md-table class="cashbox-list__table container container_30">
      <template v-if="list.length && !isLoading">
        <md-table-row class="cashbox-list_row">
          <md-table-head>{{ i18n.lbl_source_amount() }}</md-table-head>
          <md-table-head>{{ i18n.lbl_destination_amount() }}</md-table-head>
          <md-table-head>{{ i18n.lbl_status() }}</md-table-head>
          <md-table-head>{{ i18n.lbl_requestor() }}</md-table-head>
          <md-table-head>{{ i18n.withdraw_creation_date() }}</md-table-head>
          <md-table-head>ID</md-table-head>
        </md-table-row>
        <template v-for="(item, i) in list">
          <md-table-row class="cashbox-list__row" :key="i" @dblclick="openDetails(item.id)">
            <md-table-cell class="cashbox-list__table-cell source-cell">
              {{ roundCurrency(item.amount, item.destAssetCode) }}&nbsp;{{ item.destAssetCode }}
            </md-table-cell>
            <md-table-cell class="cashbox-list__table-cell destination-cell">
              {{ roundCurrency(item.destAmount, item.destAssetCode) }}&nbsp;{{ item.destAssetCode }}
            </md-table-cell>
            <md-table-cell v-bind:style="{color: statusColor(item.requestStateI)}"
                           class="status-cell cashbox-list__table-cell">
              {{ getRequestStateText(item.requestStateI) }}
            </md-table-cell>
            <md-table-cell class="cashbox-list__table-cell requestor-cell">{{ getRequestor(item) }}</md-table-cell>
            <md-table-cell class="cashbox-list__table-cell date-cell">{{item.created}}</md-table-cell>
            <md-table-cell class="cashbox-list__table-cell id-cell">{{item.id}}</md-table-cell>
            <md-tooltip md-direction="top"><md-icon>info</md-icon>&nbsp;{{ i18n.withdraw_list_tooltip() }}</md-tooltip>
          </md-table-row>
        </template>
        <md-table-row v-if="!isLoaded">
          <md-table-head></md-table-head>
          <md-table-head></md-table-head>
          <md-table-head></md-table-head>
          <div class="cashbox-list__btn-more">
            <md-button class="md-icon-button" @click="more" :disabled="isLoading">
              <md-icon>keyboard_arrow_down</md-icon>
              <md-tooltip md-direction="bottom">
                {{ i18n.lbl_view_more() }}
              </md-tooltip>
            </md-button>
          </div>
        </md-table-row>
      </template>
      <template v-else-if="isLoading">
        <div class="cashbox-list__no-withdraws">
          <p>{{ i18n.lbl_loading() }}</p>
        </div>
      </template>
      <template v-else>
        <div class="cashbox-list__no-withdraws">
          <md-icon class="md-size-4x">local_atm</md-icon>
          <h2>{{ i18n.withdraw_no_withdrawals() }}</h2>
          <br/>
          <p>{{ i18n.withdraw_no_withdrawals_desc() }}</p>
        </div>
      </template>
    </md-table>
  </div>

  <div v-else class="cashbox-list__details-wrp">
    <cashbox-details :id="id" @close-details="closeDetails"/>
  </div>
</template>

<script>
  import Searcher from './Cashbox.Searcher'
  import CashboxDetails from './Cashbox.Details'
  import { i18n } from '@js/i18n'
  import get from 'lodash/get'

  import { mapGetters, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { REQUEST_STATES } from '@const'
  import { formatDate } from '@utils/dates.util'
  import CurrencyRound from '@com/mixins/currency.round.mixin'

  export default {
    mixins: [CurrencyRound],
    components: { Searcher, CashboxDetails },
    data: _ => ({
      i18n,
      REQUEST_STATES,
      isLoading: false,
      filters: {
        asset: '',
        state: 0,
        requestor: '',
        created: {
          from: '',
          to: ''
        },
        createdStrings: {
          startDate: null,
          endDate: null
        },
        restrict: {
          notaid: true,
          cashable: true
        }
      },
      restoreFilters: {
        tokenCode: '',
        state: 0,
        requestor: '',
        startDate: null,
        endDate: null
      },
      details: [],
      showDetails: false,
      id: ''
    }),
    created () {
    },
    destroyed () {
      this.clearView()
    },
    computed: {
      ...mapGetters([
        vuexTypes.withdrawals,
        vuexTypes.accountId,
        vuexTypes.userCashTokens
      ]),
      list () {
        return this.parseRecords(get(this.withdrawals, 'records', []))
      },
      isLoaded () {
        return get(this.withdrawals, 'isLoaded')
      },
      tokenCodes () {
        return this.userCashTokens.map(token => token.code)
      }
    },

    methods: {
      ...mapActions({
        loadList: vuexTypes.GET_WITHDRAWALS,
        loadNext: vuexTypes.NEXT_WITHDRAWALS
      }),
      statusColor (stateI) {
        switch (stateI) {
          case this.REQUEST_STATES.approved:
            return '#578ebd'
          case this.REQUEST_STATES.rejected:
          case this.REQUEST_STATES.permanentlyRejected:
            return '#c2243b'
        }
        return '#3f4244'
      },
      parseRecords (records) {
        this.details = []
        let parsedDetails = []
        for (let i in records) {
          let details = {}
          details['id'] = records[i].id
          details['requestHash'] = records[i].hash
          details['requestState'] = records[i].request_state
          details['requestStateI'] = records[i].request_state_i
          details['rejectReason'] = records[i].reject_reason
          details['created'] = formatDate(records[i].created_at, 'DD MMM YYYY hh:mm:ss')
          details['updated'] = formatDate(records[i].updated_at, 'DD MMM YYYY hh:mm:ss')
          details['requestorId'] = records[i].requestor
          details['reviewer'] = records[i].reviewer
          details['signerName'] = records[i].signer_name ? records[i].signer_name : ''
          details['amount'] = this.getFiatFloat(records[i].details.withdraw.amount, 2)
          details['destAssetCode'] = records[i].details.withdraw.dest_asset_code
          details['destAmount'] = this.getFiatFloat(records[i].details.withdraw.dest_asset_amount, 2)
          details['fixedFee'] = this.getFiatFloat(records[i].details.withdraw.fixed_fee, 4)
          details['percentFee'] = this.getFiatFloat(records[i].details.withdraw.percent_fee, 4)
          details['totalFee'] = this.getFiatFloat((Number(records[i].details.withdraw.fixed_fee) +
            Number(records[i].details.withdraw.percent_fee)).toString(), 4)
          details['requestor'] = (records[i].details.withdraw.external_details &&
            records[i].details.withdraw.external_details.email)
            ? records[i].details.withdraw.external_details.email
            : ''
          details['destination'] = (records[i].details.withdraw.external_details &&
            records[i].details.withdraw.external_details.address)
            ? records[i].details.withdraw.external_details.address
            : ''
          this.details.push({id: details.id, details})
          parsedDetails.push(details)
        }
        return parsedDetails
      },
      getRequest (id) {
        const details = this.details.filter(data => data.id === id)
        return details ? details[0].details : {}
      },
      getRestoreFilters () {
        const params = {
          tokenCode: this.filters.asset,
          state: this.filters.state,
          requestor: this.filters.requestor,
          startDate: this.filters.createdStrings.startDate,
          endDate: this.filters.createdStrings.endDate
        }
        this.clearView()
        return params
      },
      getRequestStateText (stateI) {
        let stateText = ''
        switch (stateI) {
          case this.REQUEST_STATES.pending:
            stateText = i18n.withdraw_pending()
            break
          case this.REQUEST_STATES.approved:
            stateText = i18n.withdraw_approved()
            break
          case this.REQUEST_STATES.cancelled:
            stateText = i18n.withdraw_cancelled()
            break
          case this.REQUEST_STATES.rejected:
            stateText = i18n.withdraw_rejected()
            break
          case this.REQUEST_STATES.permanentlyRejected:
            stateText = i18n.withdraw_permanently_rejected()
            break
        }
        return stateText
      },
      getRequestor (item) {
        if (!item) return ''
        return (item.requestorEmail && item.requestorEmail.length)
          ? item.requestorEmail
          : item.requestor
      },
      /* Returns currency values with trailing two zeros or more if needed:
        input: 12.0000000, output 12.00;
        input 0.00030, output 0.0003
      */
      getFiatFloat (strNum, sign) {
        var parts = strNum.split('.')
        if (parts.length < 2) {
          const intVal = parseInt(strNum)
          parts = [intVal.toString(), '0'.repeat(sign)]
        } else {
          let tailZero = true
          parts[1] += '0'.repeat(sign)
          for (let i = parts[1].length - 1; i > sign - 1; i--) {
            tailZero &= (parts[1][i] === '0')
            if (tailZero) parts[1] = parts[1].slice(0, i)
            else break
          }
        }
        return parts[0] + '.' + parts[1]
      },
      async loadWithdrawals () {
        this.isLoading = true
        let records = []
        try {
          records = await this.loadList(this.filters)
        } catch (e) {
          console.error(e)
        }
        this.isLoading = false
        return records
      },
      async more () {
        this.isLoading = true
        let records = []
        try {
          records = await this.loadNext(this.filters)
        } catch (e) {
          console.error(e)
        }
        if (this.filters.cursor !== undefined) {
          delete this.filters.cursor
        }
        this.isLoading = false
        return records
      },
      loadFilteredWithdrawals (filters) {
        this.filters.asset = filters.asset
        this.filters.state = filters.state
        this.filters.requestor = filters.requestor
        this.filters.created = filters.created
        this.filters.createdStrings = filters.createdStrings
        return this.loadWithdrawals()
      },
      clearView () {
        if (this.withdrawals.records) {
          this.withdrawals.records = []
          this.withdrawals.records.length = 0
        }
        this.restoreFilters.tokenCode = ''
        this.restoreFilters.state = 0
        this.restoreFilters.requestor = ''
        this.restoreFilters.startDate = null
        this.restoreFilters.endDate = null
        this.filters.asset = ''
        this.filters.state = 0
        this.filters.requestor = ''
        this.filters.created.from = ''
        this.filters.created.to = ''
        this.filters.createdStrings.startDate = null
        this.filters.createdStrings.endDate = null
      },
      openDetails (id) {
        this.id = id
        this.showDetails = true
      },
      closeDetails (restoreFilters) {
        this.id = ''
        this.showDetails = false
        this.restoreFilters = restoreFilters
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '~scss/mixins';
  @import '~scss/variables';

  .cashbox-list__table {
    margin-top: 1.5rem;
  }

 /* .cashbox-list__view-details-btn {
    float: right;
  }*/

  .cashbox-list__no-withdraws {
    margin: 1rem auto;
    text-align: center;
  }

  .status-cell { width: 20%; }
  .source-cell, .destination-cell { width: 15% }
  .requestor-cell { width: 30% }
  .date-cell { width: 15% }
</style>



// WEBPACK FOOTER //
// src/vue/app/cashbox/index/Cashbox.List.vue